import React from 'react'
import { Alert, Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useAgentMemory, { type AgentMemoryFile } from '../hooks/useAgentMemory'
import { extractErrorMessage } from '../utils/apitools'

const formatDate = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const SKIPPED: Record<NonNullable<AgentMemoryFile['skipped']>, string> = {
  not_text: 'This is not a text file, so it is not shown.',
  too_large: 'This file is too large to show here.',
}

/**
 * "What Claude remembers about you" (agent runner plan M4, item 5). Claude
 * keeps notes about a person, one set per customer, shared by all of their
 * chats there. This page shows the signed-in person their own, as plain text.
 * It is read-only: there is nothing to edit or delete with in this version.
 */
const AgentMemoryPage: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const customer = (router.params.customer as string) || account.customer?.name || ''
  const memory = useAgentMemory(customer)

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 0.5 }}>What Claude remembers about you</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These notes are written by the agent during your chats, and it reads them at the start of each new one. Only you can see them.
        They can&apos;t be changed or removed here yet.
      </Typography>

      {memory.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : memory.error ? (
        <Alert severity="error">{extractErrorMessage(memory.error)}</Alert>
      ) : memory.files.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="subtitle1">Nothing yet</Typography>
            <Typography variant="body2" color="text.secondary">
              The agent has not written any notes about you here.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {memory.truncated && (
            <Alert severity="info">There is more here than this page shows: some notes are left out or shown without their text.</Alert>
          )}
          {memory.files.map((file) => (
            <Card key={file.name} variant="outlined" data-testid="memory-note">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="baseline" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" component="h2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[formatDate(file.modified_at) && `Updated ${formatDate(file.modified_at)}`, formatSize(file.size)].filter(Boolean).join(' · ')}
                  </Typography>
                </Stack>
                {file.skipped ? (
                  <Typography variant="body2" color="text.secondary">{SKIPPED[file.skipped] ?? 'This file is not shown.'}</Typography>
                ) : (
                  <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>
                    {file.content}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  )
}

export default AgentMemoryPage
